import React from "react";
import "@/App.css";
import "@/styles/policy.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import LicensingPage from "@/pages/LicensingPage";
import AccessibilityPage from "@/pages/AccessibilityPage";
import ContactPage from "@/pages/ContactPage";
import PaymentsPage from "@/pages/PaymentsPage";
import LeadershipPage from "@/pages/LeadershipPage";
import InsuranceSolutionsPage from "@/pages/InsuranceSolutionsPage";
import ServicePage from "@/pages/ServicePage";
import VideoLibraryPage from "@/pages/VideoLibraryPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminVideosPage from "@/pages/AdminVideosPage";
import AdminPortalUsersPage from "@/pages/AdminPortalUsersPage";
import AdminIntegrationsPage from "@/pages/AdminIntegrationsPage";
import AdminBlogPage from "@/pages/AdminBlogPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import VideoSharePage from "@/pages/VideoSharePage";
import BlogListPage from "@/pages/BlogListPage";
import BlogCategoryPage from "@/pages/BlogCategoryPage";
import BlogPostPage from "@/pages/BlogPostPage";
import CustomerPortalLoginPage from "@/pages/CustomerPortalLoginPage";
import CustomerPortalVerifyPage from "@/pages/CustomerPortalVerifyPage";
import CustomerPortalDashboardPage from "@/pages/CustomerPortalDashboardPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import MobileCallFab from "@/components/MobileCallFab";
import useVisitTracking from "@/lib/useVisitTracking";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";

// Hoisted out of render so the same object identity is reused on every
// render — prevents <Toaster /> from unnecessarily re-applying style props.
const TOAST_OPTIONS = {
    style: {
        background: "#0b0e13",
        color: "#f5ecd9",
        border: "1px solid rgba(212,175,55,0.35)",
    },
};

// Lives inside <BrowserRouter> so the hook can read useLocation().
// Renders nothing — its sole job is to fire the visit-tracking ping on
// every SPA route change.
function VisitTracker() {
    useVisitTracking();
    return null;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <VisitTracker />
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/licensing" element={<LicensingPage />} />
                        <Route path="/accessibility" element={<AccessibilityPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/pay-premium" element={<PaymentsPage />} />
                        {/* Convenience aliases for the premium-payment page */}
                        <Route path="/pay" element={<Navigate to="/pay-premium" replace />} />
                        <Route path="/payments" element={<Navigate to="/pay-premium" replace />} />
                        {/* Leadership / About — Meet Our Leadership Team */}
                        <Route path="/leadership" element={<LeadershipPage />} />
                        <Route path="/about" element={<Navigate to="/leadership" replace />} />
                        <Route path="/team" element={<Navigate to="/leadership" replace />} />
                        <Route
                            path="/insurance-solutions"
                            element={<InsuranceSolutionsPage />}
                        />
                        {/* Topic / business-line pages — single ServicePage component
                            renders the correct content based on the slug param */}
                        <Route path="/life-insurance" element={<ServicePage slug="life-insurance" />} />
                        <Route path="/supplemental-benefits" element={<ServicePage slug="supplemental-benefits" />} />
                        <Route path="/health-insurance" element={<ServicePage slug="health-insurance" />} />
                        <Route path="/personal-insurance" element={<ServicePage slug="personal-insurance" />} />
                        <Route path="/business-insurance" element={<ServicePage slug="business-insurance" />} />
                        <Route path="/commercial-lines" element={<ServicePage slug="commercial-lines" />} />
                        <Route path="/cyber-insurance" element={<ServicePage slug="cyber-insurance" />} />
                        <Route path="/extreme-weather" element={<ServicePage slug="extreme-weather" />} />
                        <Route path="/housing-community-services" element={<ServicePage slug="housing-community-services" />} />
                        {/* Backward-compat redirect — older slug remains crawlable */}
                        <Route path="/homeless-housing-services" element={<Navigate to="/housing-community-services" replace />} />
                        {/* Insurance Education Video Library — canonical + shortcut */}
                        <Route path="/insurance-education-videos" element={<VideoLibraryPage />} />
                        <Route path="/videos" element={<Navigate to="/insurance-education-videos" replace />} />
                        {/* Dedicated single-video share pages — marketing-friendly */}
                        <Route path="/video/:slug" element={<VideoSharePage />} />
                        <Route path="/videos/:slug" element={<VideoSharePage />} />
                        {/* Customer Portal — magic-link sign-in flow + dashboard */}
                        <Route path="/customer-portal" element={<Navigate to="/customer-portal/login" replace />} />
                        <Route path="/customer-portal/login" element={<CustomerPortalLoginPage />} />
                        <Route path="/customer-portal/verify" element={<CustomerPortalVerifyPage />} />
                        <Route path="/customer-portal/dashboard" element={<CustomerPortalDashboardPage />} />
                        {/* Admin */}
                        <Route path="/admin" element={<Navigate to="/admin/videos" replace />} />
                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route path="/admin/videos" element={<AdminVideosPage />} />
                        <Route path="/admin/portal-users" element={<AdminPortalUsersPage />} />
                        <Route path="/admin/customers" element={<Navigate to="/admin/portal-users" replace />} />
                        <Route path="/admin/port-users" element={<Navigate to="/admin/portal-users" replace />} />
                        <Route path="/admin/users" element={<Navigate to="/admin/portal-users" replace />} />
                        <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
                        <Route path="/admin/settings" element={<Navigate to="/admin/integrations" replace />} />
                        <Route path="/admin/blog" element={<AdminBlogPage />} />
                        <Route path="/admin/posts" element={<Navigate to="/admin/blog" replace />} />
                        <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                        <Route path="/admin/visits" element={<Navigate to="/admin/analytics" replace />} />
                        {/* Public Agency Blog */}
                        <Route path="/blog" element={<BlogListPage />} />
                        <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
                        <Route path="/blog/:slug" element={<BlogPostPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    <MobileCallFab />
                    <ScrollToTopButton />
                </AuthProvider>
            </BrowserRouter>
            <Toaster
                position="top-center"
                theme="dark"
                toastOptions={TOAST_OPTIONS}
            />
        </div>
    );
}

export default App;
